import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileCheck2, X } from "lucide-react";

const MAX = 15 * 1024 * 1024;

const shrink = (file) => new Promise((resolve) => {
  if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type) || file.size < 1.5 * 1024 * 1024) return resolve(file);
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const k = Math.min(1, 1800 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => resolve(b ? new File([b], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const ReceiptPicker = ({ file, onChange }) => {
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(false);
  useEffect(() => {
    if (!file || !/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) { setPreview(null); return; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const pick = async (f) => {
    if (!f) return;
    const ok = f.type.startsWith("image/") || f.type === "application/pdf" || /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(f.name);
    if (!ok) return toast.error("Yalnız şəkil və ya PDF yükləyin");
    const out = await shrink(f);
    if (out.size > MAX) return toast.error("Fayl maksimum 15 MB olmalıdır");
    onChange(out);
  };

  return (
    <div className="space-y-2" data-testid="receipt-picker">
      <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Ödəniş çeki (qəbz)</span>
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-4 flex items-center gap-4 transition-colors ${file ? "border-emerald-500/60 bg-emerald-500/5" : drag ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/15 hover:border-[#D4AF37]/60"}`}>
        {preview ? <img src={preview} alt="qəbz" className="w-16 h-16 rounded-lg object-cover" /> : file ? <FileCheck2 className="text-emerald-400" size={28} /> : <Upload className="text-[#D4AF37]" size={28} />}
        <div className="text-sm min-w-0 flex-1">
          <p className="font-semibold truncate" data-testid="receipt-file-name">{file ? file.name : "Qəbzin şəklini seçin və ya bura atın"}</p>
          <p className="text-xs text-zinc-500">JPG, PNG, WEBP, HEIC və ya PDF · maks. 15 MB</p>
        </div>
        <input type="file" accept="image/*,application/pdf" className="hidden"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="receipt-file-input" />
      </label>
      {file && <button type="button" onClick={() => onChange(null)} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1" data-testid="receipt-remove-btn"><X size={12} />Faylı sil</button>}
    </div>
  );
};
